import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/Topbar";
import ConfirmDialog from "@/components/ConfirmDialog";
import ModeSelectorCards from "@/components/editor/ModeSelectorCards";
import { toast } from "sonner";
import { Plus, FolderOpen, Trash2, Film, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  if (status === "rendering") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-sky-300 bg-sky-500/10 border border-sky-500/30 px-2 py-1 rounded-md">
        <Loader2 size={11} className="animate-spin" /> Wird gerendert
      </span>
    );
  }
  if (status === "rendered") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded-md">
        <CheckCircle2 size={11} /> Fertig gerendert
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-md">
        <AlertTriangle size={11} /> Render fehlgeschlagen
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#9a9aa3] bg-[#1f1f23] border border-[#2a2a30] px-2 py-1 rounded-md">
      Entwurf
    </span>
  );
}

function CreateDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState("fastcut");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) {
      setTitle("");
      setMode("fastcut");
    }
  }, [open]);
  if (!open) return null;

  const onCreate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/projects", {
        title: title.trim() || "Neues Projekt",
        render_mode: mode,
      });
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="editor-create-overlay"
    >
      <div
        className="relative w-full max-w-3xl rounded-2xl bg-[#17171a] border border-[#2a2a30] p-6 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Neues Projekt</h2>
        <p className="text-sm text-[#9a9aa3] mt-1">Titel wählen und Rendering-Modus festlegen.</p>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="z. B. Strandtag Bali"
          className="mt-5 w-full h-11 px-3.5 rounded-lg bg-[#1f1f23] border border-[#2a2a30] focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none text-white"
          data-testid="editor-create-title-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && title.trim()) onCreate();
            if (e.key === "Escape") onClose();
          }}
        />

        <div className="mt-6">
          <div className="text-xs uppercase tracking-wide text-[#9a9aa3] mb-3">
            Rendering-Modus
          </div>
          <ModeSelectorCards value={mode} onSelect={setMode} />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-[#2a2a30] text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors disabled:opacity-50"
            data-testid="editor-create-cancel-btn"
          >
            Abbrechen
          </button>
          <button
            onClick={onCreate}
            disabled={busy}
            className="inline-flex items-center justify-center h-10 px-5 rounded-lg bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold disabled:opacity-60"
            data-testid="editor-create-confirm"
          >
            {busy ? "Erstelle..." : "Erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EditorList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [confirming, setConfirming] = useState(null);

  // id → slug map used to synthesize thumbnail URLs for the FIRST clip of
  // draft projects. Rendered projects use `output_slug` directly.
  const videoIdToSlug = useMemo(() => {
    const m = {};
    for (const v of videos) if (v.id && v.slug) m[v.id] = v.slug;
    return m;
  }, [videos]);

  const load = async () => {
    setLoading(true);
    try {
      const [proj, vids] = await Promise.all([
        api.get("/projects"),
        api.get("/videos"),
      ]);
      setProjects(proj.data);
      setVideos(vids.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const thumbnailFor = (p) => {
    // Prefer the rendered output thumbnail — it's the final artefact the user
    // actually cares about. Fall back to the first source clip so drafts
    // still get a visual anchor. Everything else → null (placeholder tile).
    if (p.output_slug) {
      return `${API_BASE}/videos/${p.output_slug}/thumbnail`;
    }
    const first = p.clips?.[0];
    const slug = first && videoIdToSlug[first.video_id];
    return slug ? `${API_BASE}/videos/${slug}/thumbnail` : null;
  };

  const onCreated = (proj) => {
    setShowCreate(false);
    navigate(`/admin/editor/${proj.id}`);
  };

  const onConfirmDelete = async () => {
    if (!confirming) return;
    try {
      await api.delete(`/projects/${confirming.id}`);
      toast.success("Projekt gelöscht");
      setConfirming(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="editor-heading">
              Editor-Projekte
            </h1>
            <p className="text-sm text-[#9a9aa3] mt-1.5">
              Schneide und kombiniere Clips zu neuen Videos.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold shadow-[0_8px_24px_rgba(255,75,80,0.3)]"
            data-testid="editor-new-project-button"
          >
            <Plus size={16} />
            Neues Projekt
          </button>
        </div>

        {!loading && projects.length === 0 && (
          <div
            className="rounded-2xl border border-dashed border-[#2a2a30] bg-[#17171a]/40 p-10 text-center"
            data-testid="editor-empty"
          >
            <div className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-[#1f1f23] mb-4">
              <Film size={26} className="text-[#9a9aa3]" />
            </div>
            <h2 className="text-lg font-semibold">Noch keine Projekte</h2>
            <p className="text-sm text-[#9a9aa3] mt-1.5 max-w-md mx-auto">
              Lege ein Projekt an, füge Clips aus deiner Bibliothek hinzu, trimme sie und render
              das Endergebnis als neues Video.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="mt-6 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold"
              data-testid="editor-empty-cta"
            >
              <Plus size={16} />
              Erstes Projekt anlegen
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="editor-project-grid">
          {projects.map((p) => {
            const thumb = thumbnailFor(p);
            return (
              <article
                key={p.id}
                data-testid={`editor-project-card-${p.id}`}
                className="rounded-2xl border border-[#2a2a30] bg-[#17171a] overflow-hidden transition-all hover:border-[#3a3a40] hover:shadow-[0_12px_40px_-12px_rgba(255,75,80,0.25)]"
              >
                <Link
                  to={`/admin/editor/${p.id}`}
                  className="block relative aspect-video bg-[#0a0a0b] overflow-hidden"
                  data-testid={`editor-project-thumb-${p.id}`}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={p.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#3a3a40]">
                      <Film size={40} />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex items-center gap-1.5">
                    {p.status === "rendered" && p.render_warning && (
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-md text-amber-300 bg-amber-500/10 border border-amber-500/40"
                        title={p.render_warning}
                        data-testid={`editor-card-warning-${p.id}`}
                      >
                        <AlertTriangle size={11} />
                      </span>
                    )}
                    <StatusBadge status={p.status} />
                  </div>
                </Link>
                <div className="p-5 pt-4">
                  <h3
                    className="font-semibold text-white text-base leading-snug line-clamp-2"
                    title={p.title}
                  >
                    {p.title}
                  </h3>
                  <div className="mt-2 text-xs text-[#9a9aa3] flex items-center gap-2">
                    <span>{p.clips.length} {p.clips.length === 1 ? "Clip" : "Clips"}</span>
                    <span className="text-[#3a3a40]">·</span>
                    <span>{formatDate(p.updated_at)}</span>
                  </div>
                  {p.render_error && (
                    <p className="mt-2 text-xs text-amber-300 line-clamp-2" title={p.render_error}>
                      {p.render_error}
                    </p>
                  )}
                  <div className="mt-4 flex items-center gap-1">
                    <Link
                      to={`/admin/editor/${p.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9a9aa3] hover:text-white px-2.5 py-1.5 rounded-md hover:bg-[#1f1f23] transition-colors"
                      data-testid={`editor-open-${p.id}`}
                    >
                      <FolderOpen size={14} /> Öffnen
                    </Link>
                    <button
                      onClick={() => setConfirming(p)}
                      className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2.5 py-1.5 rounded-md transition-colors"
                      data-testid={`editor-delete-${p.id}`}
                    >
                      <Trash2 size={14} /> Löschen
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </main>

      <CreateDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={onCreated} />
      <ConfirmDialog
        open={!!confirming}
        title="Projekt löschen?"
        body={
          confirming
            ? `„${confirming.title}" wird gelöscht. Eventuell bereits gerenderte Videos bleiben in der Bibliothek.`
            : ""
        }
        confirmLabel="Endgültig löschen"
        destructive
        onCancel={() => setConfirming(null)}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}
